import { httpClient } from '@/services/http/httpClient'
import { getQueryParams, type QueryParams } from '@/utils/queryParams'

import type { IdeaStatus } from '../types'

export type Workflow = {
  id: string
  name: string
  campaignId: string
  steps: {
    id: string
    name: string
    completed: boolean
    tasks: {
      id: string
      name: string
      completed: boolean
    }[]
  }[]
}

export type ListIdeaHttpResponse = {
  id: string
  title: string
  kanbanStep: string
  tennantId: string
  campaignId: string
  kanbanStepId: string
  score: number
  sequence: string
  likes: number
  description: string
  createdAt: string
  ideaApprovals: {
    id: string
    ideaId: string
    description: string
    approvedByUser: {
      id: string
      name: string
      email: string
      image?: string
    }
  } | null
  ideaRecuse: {
    id: string
    description: string
    ideaId: string
    recusedById: string | null
    updatedAt: string
    createdAt: string
  } | null
  ideaUsers: {
    user: {
      id: string
      name: string
      email: string
      image?: string
      status?: string
      area?: {
        id: string
        name: string
      }
    }
  }[]
  campaign: {
    id: string
    sequence: string
    title: string
  }
  ideaStatuses: {
    name: string
    customName: string | null
    color: string
    customColor: string | null
  }
  ideaTags: {
    id: string
    tag: {
      id: string
      name: string
      color: string
      textColor: string
    }
  }[]
  ideaAITags: {
    aiTag: {
      name: string
    }
  }[]
  similarIdeasCount: number
  roi: number
  kanbanStepSequence: number
  secondaryLinks: {
    id: string
    title: string
  }[]
  isExcludable: boolean
  isEditable: boolean
  workflow: Workflow
  views: number
}

export type KanbanListIdea = {
  id: string
  sequence: string
  title: string
  ideasCount: number
}

type IdeaHttpResponse = {
  ideas: ListIdeaHttpResponse[]
  countIdeas: {
    all: number
    kanban: KanbanListIdea[]
  }
  paginate: {
    total: number
    page: number
    pages: number
    limit: number
  }
}

type ListIdeaParams = {
  query: QueryParams
}

export type ListIdea = {
  id: string
  sequence: string
  title: string
  description: string
  kanbanStepId: string
  ideaApprovals: {
    id: string
    ideaId: string
    description: string
    approvedByUser: {
      id: string
      name: string
      email: string
      image?: string
    }
  } | null
  ideaRecuse: {
    id: string
    description: string
    ideaId: string
    recusedById: string | null
    updatedAt: string
    createdAt: string
  } | null
  ideaStatuses: {
    name: IdeaStatus | string
    customName: string | null
    color: string
    customColor: string | null
  }
  likes: number
  views: number
  createdAt: string
  participants: {
    id: string
    name: string
    email: string
    image?: string
    status?: string
    area?: {
      id: string
      name: string
    }
  }[]
  challenge: {
    id: string
    sequence: string
    title: string
  }
  ideaTags: {
    id: string
    tag: {
      id: string
      name: string
      color: string
      textColor: string
    }
  }[]
  ideaAITags: {
    aiTag: {
      name: string
    }
  }[]
  similarIdeasCount: number
  workflow: Workflow
  secondaryLinks: {
    id: string
    title: string
  }[]
  score: number
  roi: number
}

export type ListIdeaResponse = {
  ideas: ListIdea[]
  countIdeas: {
    all: number
    kanban: KanbanListIdea[]
  }
  paginate: {
    total: number
    page: number
    pages: number
    limit: number
  }
}

export async function listIdeas({
  query,
}: ListIdeaParams): Promise<ListIdeaResponse> {
  const { workflows, ...rest } = query as QueryParams & {
    workflows?: string[]
  }
  const queryParams = getQueryParams({
    ...rest,
    ...(workflows !== undefined
      ? { workflows: JSON.stringify(workflows) }
      : {}),
  })

  const { data } = await httpClient.get<IdeaHttpResponse>(
    `ideas/ideas?${queryParams}`,
  )
  return {
    ideas: data.ideas.map((idea) => ({
      id: idea.id,
      sequence: idea.sequence,
      title: idea.title,
      description: idea.description,
      likes: idea.likes,
      views: idea.views,
      score: idea.score,
      roi: idea.roi,
      kanbanStepId: idea.kanbanStepId,
      ideaApprovals: idea.ideaApprovals,
      ideaRecuse: idea.ideaRecuse,
      createdAt: new Date(idea.createdAt).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
      ideaStatuses: idea.ideaStatuses
        ? {
            name: idea.ideaStatuses.name,
            customName: idea.ideaStatuses.customName,
            color: idea.ideaStatuses.color,
            customColor: idea.ideaStatuses.customColor,
          }
        : {
            name: 'Sem status',
            customName: null,
            color: '#000',
            customColor: null,
          },
      ideaTags: idea.ideaTags,
      ideaAITags: idea.ideaAITags,
      similarIdeasCount: idea.similarIdeasCount,
      secondaryLinks: idea.secondaryLinks,
      participants:
        idea.ideaUsers.map((ideaUser) => ({
          id: ideaUser.user.id,
          name: ideaUser.user.name,
          email: ideaUser.user.email,
          image: ideaUser.user.image,
          area: ideaUser.user.area,
          status: ideaUser.user.status,
        })) || [],
      challenge: {
        id: idea.campaign.id,
        sequence: `#${String(idea.campaign.sequence).padStart(2, '0')}`,
        title: idea.campaign.title,
      },
      workflow: idea.workflow,
    })),
    countIdeas: data.countIdeas,
    paginate: {
      total: data.paginate.total,
      limit: data.paginate.limit,
      page: data.paginate.page,
      pages: data.paginate.pages,
    },
  }
}
